import { StrictMode, useEffect, useRef } from 'react';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import App from 'App';
import { getApiBaseUrlConfigurationError } from 'config/apiBaseUrl';
import { ApiConfigurationErrorScreen } from 'config/ApiConfigurationErrorScreen';
import { getStoredFlowState } from 'pages/organizationFlow/flowStorage';
import { createRoot } from 'react-dom/client';
import { Provider, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { setEuId, setOrgId, setUnitId } from 'store/slices/authSlice';
import { setCurrentStep } from 'store/slices/flowSlice';
import { persistor, store } from 'store/store';
import theme from 'theme';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import { getJwtUnitId } from 'utils/jwtUnitClaims';

import { getUserIdToken, getOrgId, purgeLegacyTokenStorage } from 'services/auth';
import { initMixpanel } from 'services/mixpanel';

import { hydrateStoreFromTabSession } from './services/auth/sessionTokenTabStorage';

import { getSessionRealm } from '@/services/auth/sessionRealm';

import 'styles/appFonts';
import 'index.css';

// Analytics must never block bootstrap — initMixpanel no-ops without a token.
initMixpanel();

if (typeof window !== 'undefined' && !('global' in window)) {
  (window as Window & typeof globalThis & { global?: typeof globalThis }).global = window;
}

// Single Emotion cache so MUI/Emotion don't keep injecting new style tags into head
const emotionCache = createCache({ key: 'spence', prepend: true });

/** Syncs orgId and flow view from storage into Redux once on mount. */
function HydrateFromStorage({ children }: Readonly<{ children: React.ReactNode }>) {
  const dispatch = useDispatch();
  const didHydrateTabSessionRef = useRef(false);
  // Restore tokens during the first render — child session validation effects run before this useEffect.
  if (!didHydrateTabSessionRef.current) {
    didHydrateTabSessionRef.current = true;
    hydrateStoreFromTabSession(dispatch);
  }

  useEffect(() => {
    const id = getOrgId();
    if (id) dispatch(setOrgId(id));
    purgeLegacyTokenStorage();
    const euId = window.localStorage.getItem('euId');
    if (euId) dispatch(setEuId(euId));
    const realm = getSessionRealm();
    const idToken = getUserIdToken();
    if (idToken && realm === 'user') {
      const unitIdFromToken = getJwtUnitId(idToken);
      if (unitIdFromToken) {
        dispatch(setUnitId(unitIdFromToken));
        if (typeof window.localStorage?.setItem === 'function') {
          window.localStorage.setItem('unitId', unitIdFromToken);
        }
      }
    }

    const flowStored = getStoredFlowState();
    if (flowStored?.view) dispatch(setCurrentStep(flowStored.view));
  }, [dispatch]);
  return <>{children}</>;
}

const container = document.getElementById('root')!;
const root = createRoot(container);
const apiBaseUrlConfigurationError = getApiBaseUrlConfigurationError();

root.render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {apiBaseUrlConfigurationError ? (
        <ApiConfigurationErrorScreen message={apiBaseUrlConfigurationError} />
      ) : (
        <Provider store={store}>
          <PersistGate
            loading={null}
            persistor={persistor}
            onBeforeLift={() => {
              hydrateStoreFromTabSession(store.dispatch);
            }}
          >
            <HydrateFromStorage>
              <CacheProvider value={emotionCache}>
                <App />
              </CacheProvider>
            </HydrateFromStorage>
          </PersistGate>
        </Provider>
      )}
    </ThemeProvider>
  </StrictMode>
);
